import { hash } from "daedalus-core";
import type { CropDefinition, CountRange, QualityWeights } from "./crop-types.ts";
import type { CropState } from "./crop-growth.ts";
import type { FarmingRules } from "./farming.ts";

/**
 * What a harvest gives (ADR-144 decision 15): the yield, its quality and the seeds back, rolled from the crop's own seed and
 * how well it was watered, never from the clock or `Math.random()`. The server's harvest, the farm store's banking and the Crop
 * editor's preview call the same functions and get the same answer.
 */

/**
 * The quality of a poorly watered crop's stack. Quality is a stack property: stacks of different quality do not merge.
 */
export const QUALITY_POOR = 0;

/**
 * The quality of an ordinary stack.
 */
export const QUALITY_NORMAL = 1;

/**
 * The quality of a well-watered crop's stack.
 */
export const QUALITY_FINE = 2;

/**
 * The three qualities a produce stack may have.
 */
export type CropQuality = typeof QUALITY_POOR | typeof QUALITY_NORMAL | typeof QUALITY_FINE;

/**
 * One stack of produce a harvest gives.
 */
export interface HarvestedItem {
  readonly item: string;
  readonly quantity: number;
  readonly quality: CropQuality;
}

/**
 * What one harvest gives: the produce and the seed bags back.
 */
export interface Harvest {
  readonly produce: readonly HarvestedItem[];
  readonly seeds: number;
}

/**
 * The result of harvesting a crop: what it gave, and the crop that stands in its place (null when the plant is removed).
 */
export interface HarvestResult {
  readonly harvest: Harvest;
  readonly remaining: CropState | null;
}

const BASIS_POINTS = 10000;
const UNIT_RANGE = 1 << 24;
const QUALITY_DOMAIN = 11;
const COUNT_DOMAIN = 12;
const SEED_CHANCE_DOMAIN = 13;
const SEED_COUNT_DOMAIN = 14;

/**
 * How well a crop was watered: the moist share of its growing hours, in basis points. A crop with no growing hours reads as a
 * full score.
 *
 * @param state The crop.
 * @returns A whole number from 0 to 10000.
 */
export function wateringScoreBp(state: CropState): number {
  return state.growHours === 0 ? BASIS_POINTS : Math.floor((state.wetHours * BASIS_POINTS) / state.growHours);
}

/**
 * Shifts a yield entry's quality weights by the watering score: a score at or past the fine cut-off moves the poor weight to
 * fine, one under the poor cut-off moves the fine weight to poor, and one between leaves them.
 *
 * @param weights The entry's weights.
 * @param scoreBp The watering score in basis points.
 * @param rules The farming rules.
 * @returns The weights to roll with.
 */
export function qualityWeights(weights: QualityWeights, scoreBp: number, rules: FarmingRules): QualityWeights {
  if (scoreBp >= rules.qualityFineAtPercent * 100) return { poor: 0, normal: weights.normal, fine: weights.fine + weights.poor };
  if (scoreBp < rules.qualityPoorBelowPercent * 100) return { poor: weights.poor + weights.fine, normal: weights.normal, fine: 0 };
  return weights;
}

/**
 * A whole number in `[0, range)` drawn from the crop's seed.
 *
 * @param cropSeed The crop's seed.
 * @param domain The kind of draw.
 * @param index The entry the draw is for.
 * @param range The number of outcomes.
 * @returns The draw.
 */
function draw(cropSeed: number, domain: number, index: number, range: number): number {
  return Math.floor(((hash(cropSeed, domain, index) >>> 8) * range) / UNIT_RANGE);
}

/**
 * Rolls a count inside a range.
 *
 * @param cropSeed The crop's seed.
 * @param domain The kind of draw.
 * @param index The entry the draw is for.
 * @param range The range, both ends included.
 * @returns The count.
 */
function rollCount(cropSeed: number, domain: number, index: number, range: CountRange): number {
  return range.min + draw(cropSeed, domain, index, range.max - range.min + 1);
}

/**
 * Rolls the quality of one yield entry.
 *
 * @param cropSeed The crop's seed.
 * @param index The entry's index.
 * @param weights The weights after the watering shift.
 * @returns The quality.
 */
function rollQuality(cropSeed: number, index: number, weights: QualityWeights): CropQuality {
  const total = weights.poor + weights.normal + weights.fine;
  const pick = draw(cropSeed, QUALITY_DOMAIN, index, total);
  if (pick < weights.poor) return QUALITY_POOR;
  return pick < weights.poor + weights.normal ? QUALITY_NORMAL : QUALITY_FINE;
}

/**
 * Rolls what a crop gives. The roll depends only on the definition, the crop's watering score and `cropSeed`, so the same
 * crop gives the same harvest whichever process rolls it and whenever.
 *
 * @param def The crop's definition.
 * @param rules The farming rules.
 * @param state The crop (its counters give the watering score).
 * @param cropSeed An unsigned 32-bit number that identifies this crop, such as its row id.
 * @returns The produce and the seed bags back.
 * @throws RangeError when `cropSeed` is not an unsigned 32-bit integer.
 */
export function rollCropHarvest(def: CropDefinition, rules: FarmingRules, state: CropState, cropSeed: number): Harvest {
  const scoreBp = wateringScoreBp(state);
  const produce = def.yield.map((entry, index) => ({
    item: entry.item,
    quantity: rollCount(cropSeed, COUNT_DOMAIN, index, entry.count),
    quality: rollQuality(cropSeed, index, qualityWeights(entry.quality, scoreBp, rules))
  }));
  const back = def.seedReturn;
  const returned = draw(cropSeed, SEED_CHANCE_DOMAIN, 0, 100) < back.chance;
  let seeds = returned ? rollCount(cropSeed, SEED_COUNT_DOMAIN, 0, back.count) : 0;
  if (back.chance === 100 && scoreBp >= rules.qualityPoorBelowPercent * 100) seeds = Math.max(1, seeds);
  return { produce, seeds };
}

/**
 * Harvests a ripe crop.
 *
 * @param def The crop's definition.
 * @param rules The farming rules.
 * @param state The crop, evaluated to the time of harvest.
 * @param cropSeed An unsigned 32-bit number that identifies this crop.
 * @param nowMs The time of harvest, which a regrowing plant restarts from.
 * @returns What it gave and what stands in its place (a regrowing plant dropped back to `regrow.toStage` with its timers
 *   reset, else nothing), or null when the crop is not ripe.
 * @throws RangeError when `cropSeed` is not an unsigned 32-bit integer.
 */
export function harvestCrop(def: CropDefinition, rules: FarmingRules, state: CropState, cropSeed: number, nowMs: number): HarvestResult | null {
  if (state.status !== "ripe") return null;
  const harvest = rollCropHarvest(def, rules, state, cropSeed);
  if (def.regrow === undefined) return { harvest, remaining: null };
  const need = def.stages[def.regrow.toStage]!.water;
  const moistHours = need === "none" ? 0 : Math.min(state.moistHours, rules.waterHours[need]);
  return {
    harvest,
    remaining: { plantedAt: state.plantedAt, evaluatedAt: nowMs, stage: def.regrow.toStage, stageHours: 0, moistHours, dryHours: 0, wetHours: 0, growHours: 0, status: "growing", ripeAt: null, diedAt: null }
  };
}
